import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { DEPT_COLORS } from '../lib/deptColors'
import { useAuth } from '../hooks/useAuth'

function fmtTs(ts) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function SubtaskNoteSheet({ subtask, projectId, userProfile, onClose }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const [notes, setNotes]   = useState([])
  const [loading, setLoading] = useState(true)
  const [newNote, setNewNote] = useState('')
  const [saving, setSaving]   = useState(false)

  useEffect(() => { loadNotes() }, [subtask?.id])

  async function loadNotes() {
    if (!subtask?.id) return
    setLoading(true)
    const { data } = await supabase
      .from('project_notes')
      .select('*')
      .eq('subtask_id', subtask.id)
      .order('created_at', { ascending: false })
    setNotes(data ?? [])
    setLoading(false)
  }

  async function submitNote() {
    if (!newNote.trim() || saving) return
    setSaving(true)
    const name = userProfile?.display_name || userProfile?.email || null
    const { data, error } = await supabase
      .from('project_notes')
      .insert({
        project_id:    projectId,
        subtask_id:    subtask.id,
        subtask_name:  subtask.task_name,
        body:          newNote.trim(),
        created_by:    userProfile?.id ?? null,
        created_by_name: name,
      })
      .select()
      .single()
    if (!error && data) {
      setNotes(prev => [data, ...prev])
      setNewNote('')
    }
    setSaving(false)
  }

  const deptHex = orgDeptMap[subtask?.department]?.hex || '#9ca3af'

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[80vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0 flex-1 pr-3">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {subtask?.department && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white flex-shrink-0"
                  style={{ backgroundColor: deptHex }}>
                  {subtask.department}
                </span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                subtask?.status === 'Complete' ? 'bg-green-100 text-green-700' :
                subtask?.status === 'In Work'  ? 'bg-blue-100 text-blue-700'  :
                'bg-gray-100 text-gray-600'
              }`}>{subtask?.status}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-900 leading-snug">{subtask?.task_name}</h3>
            {(subtask?.start_date || subtask?.end_date) && (
              <p className="text-xs text-gray-400 mt-0.5">
                {fmtDate(subtask?.start_date)} — {fmtDate(subtask?.end_date)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* Add note */}
          <div className="px-5 pt-4 pb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Add a note</label>
            <textarea
              rows={3}
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submitNote() }}
              placeholder="Add a note about this task..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">Ctrl+Enter to submit</span>
              <button
                onClick={submitNote}
                disabled={saving || !newNote.trim()}
                className="text-sm font-medium bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white px-4 py-1.5 rounded-lg transition-colors"
              >
                {saving ? 'Saving…' : 'Submit'}
              </button>
            </div>
          </div>

          {/* Notes list */}
          {!loading && notes.length > 0 && (
            <>
              <div className="border-t border-gray-100 mx-5" />
              <div className="px-5 py-4 space-y-3">
                {notes.map(note => (
                  <div key={note.id} className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex flex-wrap items-center gap-x-2 mb-1">
                      {note.created_by_name && (
                        <span className="text-xs font-semibold text-gray-700">{note.created_by_name}</span>
                      )}
                      <span className="text-xs text-gray-400">{fmtTs(note.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-snug">{note.body}</p>
                  </div>
                ))}
              </div>
            </>
          )}
          {!loading && notes.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-6">No notes yet for this task.</p>
          )}
          {loading && (
            <p className="text-xs text-gray-400 text-center py-6">Loading…</p>
          )}
        </div>
      </div>
    </div>
  )
}
